import { Injectable, EventEmitter, OnInit, OnDestroy } from "@angular/core";
import { BrokerService } from "./broker.service";
import { Observable, Subscription } from "rxjs";

export interface Notification {
  id?: number;
  title?: string;
  type: "success" | "info" | "warning" | "error";
  message: string;
  duration?: number;
  showClose?: boolean;
  showIfDuplicated?: boolean;
}

// export interface NotificationEvent {
//     command: string;
//     notification?: Notification;
//     cancel?: boolean;
// }

interface NotificationStore {
  sticky: Notification[];
  currentRoute: Notification[];
  nextRoute: Notification[];
  [scope: string]: Notification[];
}

@Injectable()
export class NotificationService {
  private notificationStore: NotificationStore = {
    sticky: [],
    currentRoute: [],
    nextRoute: [],
  };

  public current: EventEmitter<Notification[]>;

  constructor(private broker: BrokerService) {
    this.broker.on("navigation.ended").subscribe(() => this.updateForCurrentRoute());
    this.current = new EventEmitter<Notification[]>();
  }

  public updateForCurrentRoute() {
    let store = this.notificationStore;

    if (store.nextRoute.length > 0) {
      store.currentRoute = store.currentRoute.concat(store.nextRoute);
      store.nextRoute.forEach(this.setupRemoveTimer.bind(this));
      store.nextRoute = [];
      this.currentUpdated();
    }
  }

  public currentUpdated() {
    this.current.emit(
      [].concat(this.notificationStore.sticky, this.notificationStore.currentRoute)
    );
  }

  public push(
    message: string,
    type: "success" | "info" | "warning" | "error",
    scope?: "sticky" | "currentRoute" | "nextRoute"
  ) {
    return this.pushNotification(scope || "currentRoute", { type, message });
  }

  public pushSticky(notification: Notification): number {
    return this.pushNotification("sticky", notification);
  }

  public pushForNextRoute(notification: Notification): number {
    return this.pushNotification("nextRoute", notification);
  }

  public pushForCurrentRoute(notification: Notification): number {
    return this.pushNotification("currentRoute", notification);
  }

  private pushNotification(
    scope: "sticky" | "currentRoute" | "nextRoute",
    notification: Notification
  ): number {
    let notifications = this.notificationStore[scope],
      duration = notification.duration,
      similarNotificationExists =
        notifications.findIndex(
          (x) => x.message == notification.message && x.type === notification.type
        ) > -1;

    notification.id = notification.id || new Date().getTime();
    notification.showClose = notification.showClose || true;
    notification.duration =
      scope === "sticky" ? 0 : typeof duration === "undefined" ? 5000 : duration;

    if (!similarNotificationExists || notification.showIfDuplicated) {
      notifications.unshift(notification);
      if (scope !== "nextRoute") {
        this.currentUpdated();
        this.setupRemoveTimer(notification);
      }
    }
    return notification.id;
  }

  private setupRemoveTimer(notification: Notification) {
    if (notification.duration) {
      setTimeout(() => this.remove(notification), notification.duration);
    }
  }

  public removeById(id: number) {
    let store = this.notificationStore;
    Object.keys(store).forEach((scope: string) => {
      let notificationsByScope = store[scope];
      let idx = notificationsByScope.findIndex((notification) => notification.id === id);
      if (idx > -1) {
        notificationsByScope.splice(idx, 1);
        if (scope !== "nextRoute") {
          this.currentUpdated();
        }
      }
    });
  }

  public remove(notification: Notification) {
    let store = this.notificationStore;
    Object.keys(store).forEach((scope: string) => {
      let notificationsByScope = store[scope];
      let idx = notificationsByScope.indexOf(notification);
      if (idx > -1) {
        notificationsByScope.splice(idx, 1);
        if (scope !== "nextRoute") {
          this.currentUpdated();
        }
      }
    });
  }

  public removeAll() {
    let store = this.notificationStore;
    Object.keys(store).forEach((scope: string) => {
      store[scope].length = 0;
    });
    this.currentUpdated();
  }
}
